import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { tipPlacement } from '../../lib/stateGeometry'

/**
 * The prototype's floating help (`data-tooltip`, R72): the same words on hover,
 * focus and tap. A tap PINS a help open; Escape and a press anywhere else close
 * it. The bubble floats over the page in a portal, placed above or below its
 * trigger and kept inside the viewport, and the trigger is described by it
 * (`aria-describedby`), so a screen reader hears it on focus.
 *
 * `pin: false` is for the map's points and the timelines' date columns: their
 * click SELECTS, so hover and focus explain and nothing is pinned.
 */
export function StateTip({ text, placement = 'top', pin = true, children }: {
  text: string
  placement?: 'top' | 'bottom'
  pin?: boolean
  children: (props: TipTriggerProps) => ReactNode
}) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [trigger, setTrigger] = useState<HTMLElement | null>(null)
  const bubble = useRef<HTMLSpanElement | null>(null)
  const [style, setStyle] = useState<CSSProperties>({ top: 0, left: 0, visibility: 'hidden' })

  const close = useCallback(() => { setOpen(false); setPinned(false) }, [])

  const place = useCallback(() => {
    if (trigger === null || bubble.current === null) return
    const t = trigger.getBoundingClientRect()
    const b = bubble.current.getBoundingClientRect()
    const at = tipPlacement(t, b, { width: document.documentElement.clientWidth, height: window.innerHeight }, placement)
    setStyle({ top: at.top, left: at.left, visibility: 'visible' })
  }, [placement, trigger])

  useLayoutEffect(() => { if (open) place() }, [open, place])

  useEffect(() => {
    if (!open) return
    const outside = (event: PointerEvent) => {
      const target = event.target as Node
      if (!trigger?.contains(target) && !bubble.current?.contains(target)) close()
    }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') close() }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape)
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape)
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, close, place, trigger])

  const props: TipTriggerProps = {
    ref: setTrigger,
    'aria-describedby': id,
    ...(pin ? { 'aria-expanded': open, 'aria-controls': id } : {}),
    onPointerEnter: (event) => { if (event.pointerType === 'mouse') setOpen(true) },
    onPointerLeave: (event) => { if (event.pointerType === 'mouse' && !pinned) setOpen(false) },
    onFocus: () => setOpen(true),
    onBlur: () => { if (!pinned) setOpen(false) },
    onClick: () => {
      if (!pin) return
      if (pinned) close()
      else { setPinned(true); setOpen(true) }
    },
  }

  return <>
    {children(props)}
    {createPortal(<span ref={bubble} id={id} role="tooltip" className="bp-tip" hidden={!open}
      data-open={open ? 'true' : undefined} style={style}>{text}</span>, document.body)}
  </>
}

export interface TipTriggerProps {
  ref: (el: HTMLElement | null) => void
  'aria-describedby': string
  'aria-expanded'?: boolean
  'aria-controls'?: string
  onPointerEnter: (event: React.PointerEvent) => void
  onPointerLeave: (event: React.PointerEvent) => void
  onFocus: () => void
  onBlur: () => void
  onClick: () => void
}

/** The round "?" help trigger. */
export function HelpButton({ label, text, placement }: { label: string; text: string; placement?: 'top' | 'bottom' }) {
  return <StateTip text={text} placement={placement}>
    {(tip) => <button type="button" className="bp-info" aria-label={label} {...tip}>?</button>}
  </StateTip>
}
